import type { ApiRequestBody, ApiResponseBody, ApiSearchQuery } from '@/lib/api/api-type-helper';

// ---------------------------------------------------------------------------
// List API Types
// ---------------------------------------------------------------------------

export type GetIssueListQuery = ApiSearchQuery<'/v2/qa-force/issues', 'get'>;
export type GetIssueListResponse = ApiResponseBody<'/v2/qa-force/issues', 'get'>;
export type IssueListItem = GetIssueListResponse['items'][0];

// ---------------------------------------------------------------------------
// CSV Export API Types（簡易エクスポート: 一覧表示列を view モードで出力）
// ---------------------------------------------------------------------------

/** 簡易エクスポートのフィルタ。一覧 API と同じ絞り込みを使う（mode/encoding は固定注入）。 */
export type GetIssuesCsvFilters = GetIssueListQuery;
export type GetIssuesCsvResponse = ApiResponseBody<'/v2/qa-force/issues/csv', 'get'>;
export type IssueOrderBy = NonNullable<GetIssueListQuery>['orderBy'];
export const IssueOrderBy = {
  NAME: 'NAME',
  STATUS: 'STATUS',
  SEVERITY: 'SEVERITY',
  CREATED_AT: 'CREATED_AT'
} as const satisfies Record<string, NonNullable<IssueOrderBy>>;

// ---------------------------------------------------------------------------
// Count API Types
// ---------------------------------------------------------------------------

export type GetIssueCountQuery = ApiSearchQuery<'/v2/qa-force/issues/count', 'get'>;
export type GetIssueCountResponse = ApiResponseBody<'/v2/qa-force/issues/count', 'get'>;

// ---------------------------------------------------------------------------
// Detail API Types
// ---------------------------------------------------------------------------

export type GetIssueResponse = ApiResponseBody<'/v2/qa-force/issues/{id}', 'get'>;
export type PostIssueRequest = ApiRequestBody<'/v2/qa-force/issues', 'post'>;
export type PostIssueResponse = ApiResponseBody<'/v2/qa-force/issues', 'post'>;
export type PatchIssueRequest = ApiRequestBody<'/v2/qa-force/issues/{id}', 'patch'>;
export type PatchIssueResponse = ApiResponseBody<'/v2/qa-force/issues/{id}', 'patch'>;
export type PatchIssueStatusRequest = ApiRequestBody<'/v2/qa-force/issues/{id}/status', 'patch'>;
export type PatchIssueStatusResponse = ApiResponseBody<'/v2/qa-force/issues/{id}/status', 'patch'>;

// ---------------------------------------------------------------------------
// Dev Document / TestSet / Ticket Link Types
// ---------------------------------------------------------------------------

/**
 * 不具合に紐づく開発ドキュメント（詳細レスポンスの `devDocuments` 要素）。
 * `{ id, code, title, kind, status }`。curated（IssueDevDocument）由来のみ。
 * 紐付けの書き込みは PATCH の `devDocumentIds`（全置換）で行う
 * （専用 add/remove エンドポイントは無い）。
 */
export type IssueDevDocumentItem = GetIssueResponse['devDocuments'][number];

/**
 * 不具合に紐づくテストセット（詳細レスポンスの `testSets` 要素）。
 * `{ id, name, status, origins }`。`origins` は curated / detection / testCase の
 * 由来集合（1 TestSet が複数由来を持ちうる。dedup 済）。**編集可能なのは origins に
 * `'curated'` を含むものだけ**で、書き込みは PATCH の `testSetIds`（curated のみ全置換）
 * で行う（detection / testCase 由来は read-only）。
 */
export type IssueTestSetItem = GetIssueResponse['testSets'][number];

/** テストセットの由来（curated=手動 / detection=検出元 / testCase=テストケース）。 */
export type IssueTestSetOrigin = IssueTestSetItem['origins'][number];
export const IssueTestSetOrigin = {
  CURATED: 'curated',
  DETECTION: 'detection',
  TEST_CASE: 'testCase'
} as const satisfies Record<string, IssueTestSetOrigin>;

/**
 * 不具合に紐づく開発チケット（詳細レスポンスの `tickets` 要素）。
 * `{ id, code, title, kind, status, priority }`。「この不具合をどのチケットで直すのか」の
 * 直接紐付け（IssueTicket の N:M）由来のみで、プロダクト横断可・`code` の昇順。
 * 紐付けの書き込みは PATCH の `ticketIds`（全置換）で行う
 * （専用 add/remove エンドポイントは無い）。**同じ行集合は開発チケット側の PATCH
 * `issueIds` からも全置換される**（双方向 write）ため、部分配列を送ると
 * チケット側で作った紐付けが消える。間接経路（IssueTestSet → TestSetTicket）は
 * 併存させず、ここには直接紐付けだけが載る。
 */
export type IssueTicketItem = GetIssueResponse['tickets'][number];

// ---------------------------------------------------------------------------
// Issue Status（6 値）
//
// schema から自動推論。タブ表示する 4 値: untouched / in_progress / backlog /
// waiting_reproduction。内部値（タブ非表示）: resolved / dismissed
// ---------------------------------------------------------------------------

export type IssueStatus = IssueListItem['status'];
export const IssueStatus = {
  UNTOUCHED: 'untouched',
  IN_PROGRESS: 'in_progress',
  BACKLOG: 'backlog',
  WAITING_REPRODUCTION: 'waiting_reproduction',
  RESOLVED: 'resolved',
  DISMISSED: 'dismissed'
} as const satisfies Record<string, IssueStatus>;
const ISSUE_STATUS_VALUES: ReadonlySet<string> = new Set(Object.values(IssueStatus));
export function isIssueStatus(value: string | null | undefined): value is IssueStatus {
  return value != null && ISSUE_STATUS_VALUES.has(value);
}

/** 一覧画面のステータスフィルタ用。ドメイン値 + "ALL"。 */
export type IssueStatusFilter = IssueStatus | 'ALL';

// ---------------------------------------------------------------------------
// Issue Severity（5 値、enhanced を含む）
//
// schema から自動推論。enhanced は「改善要望（Enhancement）」を表す。
// ---------------------------------------------------------------------------

export type IssueSeverity = IssueListItem['severity'];
export const IssueSeverity = {
  CRITICAL: 'critical',
  HIGH: 'high',
  MEDIUM: 'medium',
  LOW: 'low',
  ENHANCED: 'enhanced'
} as const satisfies Record<string, IssueSeverity>;
const ISSUE_SEVERITY_VALUES: ReadonlySet<string> = new Set(Object.values(IssueSeverity));
export function isIssueSeverity(value: string | null | undefined): value is IssueSeverity {
  return value != null && ISSUE_SEVERITY_VALUES.has(value);
}

// ---------------------------------------------------------------------------
// Issue Priority（4 値、optional）
//
// BE schema enum: URGENT | HIGH | MEDIUM | LOW。値の小文字版を i18n key にする。
// ---------------------------------------------------------------------------

export const IssuePriority = {
  URGENT: 'URGENT',
  HIGH: 'HIGH',
  MEDIUM: 'MEDIUM',
  LOW: 'LOW'
} as const;
export type IssuePriority = (typeof IssuePriority)[keyof typeof IssuePriority];
const ISSUE_PRIORITY_VALUES: ReadonlySet<string> = new Set(Object.values(IssuePriority));
export function isIssuePriority(value: string | null | undefined): value is IssuePriority {
  return value != null && ISSUE_PRIORITY_VALUES.has(value);
}

/** options.priority.* i18n キー (URGENT → urgent 等の小文字版) */
export const ISSUE_PRIORITY_OPTION_KEY = {
  URGENT: 'urgent',
  HIGH: 'high',
  MEDIUM: 'medium',
  LOW: 'low'
} as const satisfies Record<IssuePriority, string>;

// ---------------------------------------------------------------------------
// Issue Phase（7 値、optional）
//
// BE schema enum: REQUIREMENTS | DESIGN | DEVELOPMENT | REVIEW | TESTING |
// STAGING | PRODUCTION。occurrencePhase / detectionPhase の両方で利用。
// ---------------------------------------------------------------------------

export const IssuePhase = {
  REQUIREMENTS: 'REQUIREMENTS',
  DESIGN: 'DESIGN',
  DEVELOPMENT: 'DEVELOPMENT',
  REVIEW: 'REVIEW',
  TESTING: 'TESTING',
  STAGING: 'STAGING',
  PRODUCTION: 'PRODUCTION'
} as const;
export type IssuePhase = (typeof IssuePhase)[keyof typeof IssuePhase];
const ISSUE_PHASE_VALUES: ReadonlySet<string> = new Set(Object.values(IssuePhase));
export function isIssuePhase(value: string | null | undefined): value is IssuePhase {
  return value != null && ISSUE_PHASE_VALUES.has(value);
}

/** options.issuePhase.* i18n キー (REQUIREMENTS → requirements 等の小文字版) */
export const ISSUE_PHASE_OPTION_KEY = {
  REQUIREMENTS: 'requirements',
  DESIGN: 'design',
  DEVELOPMENT: 'development',
  REVIEW: 'review',
  TESTING: 'testing',
  STAGING: 'staging',
  PRODUCTION: 'production'
} as const satisfies Record<IssuePhase, string>;

// ---------------------------------------------------------------------------
// External System Type（8 値、optional）
//
// BE schema enum: JIRA | GITHUB | BACKLOG | SLACK | LINEAR | ASANA | NOTION |
// OTHER。externalId とセットで使われる「外部チケット管理システムの種別」。
// ---------------------------------------------------------------------------

export const ExternalSystemType = {
  JIRA: 'JIRA',
  GITHUB: 'GITHUB',
  BACKLOG: 'BACKLOG',
  SLACK: 'SLACK',
  LINEAR: 'LINEAR',
  ASANA: 'ASANA',
  NOTION: 'NOTION',
  OTHER: 'OTHER'
} as const;
export type ExternalSystemType = (typeof ExternalSystemType)[keyof typeof ExternalSystemType];
const EXTERNAL_SYSTEM_TYPE_VALUES: ReadonlySet<string> = new Set(Object.values(ExternalSystemType));
export function isExternalSystemType(value: string | null | undefined): value is ExternalSystemType {
  return value != null && EXTERNAL_SYSTEM_TYPE_VALUES.has(value);
}

/** options.externalSystemType.* i18n キー (JIRA → jira 等の小文字版) */
export const EXTERNAL_SYSTEM_TYPE_OPTION_KEY = {
  JIRA: 'jira',
  GITHUB: 'github',
  BACKLOG: 'backlog',
  SLACK: 'slack',
  LINEAR: 'linear',
  ASANA: 'asana',
  NOTION: 'notion',
  OTHER: 'other'
} as const satisfies Record<ExternalSystemType, string>;

// ---------------------------------------------------------------------------
// Issue Cause Category（8 値、optional）
//
// BE schema enum: REQUIREMENT_GAP | SPEC_CHANGE | DESIGN_DEFECT |
// IMPLEMENTATION_ERROR | TEST_GAP | EXTERNAL_DEPENDENCY | OPERATIONAL | OTHER。
// causeAnalysis サブセクションの原因カテゴリ。
// ---------------------------------------------------------------------------

export const IssueCauseCategory = {
  REQUIREMENT_GAP: 'REQUIREMENT_GAP',
  SPEC_CHANGE: 'SPEC_CHANGE',
  DESIGN_DEFECT: 'DESIGN_DEFECT',
  IMPLEMENTATION_ERROR: 'IMPLEMENTATION_ERROR',
  TEST_GAP: 'TEST_GAP',
  EXTERNAL_DEPENDENCY: 'EXTERNAL_DEPENDENCY',
  OPERATIONAL: 'OPERATIONAL',
  OTHER: 'OTHER'
} as const;
export type IssueCauseCategory = (typeof IssueCauseCategory)[keyof typeof IssueCauseCategory];
const ISSUE_CAUSE_CATEGORY_VALUES: ReadonlySet<string> = new Set(Object.values(IssueCauseCategory));
export function isIssueCauseCategory(value: string | null | undefined): value is IssueCauseCategory {
  return value != null && ISSUE_CAUSE_CATEGORY_VALUES.has(value);
}

/** options.causeCategory.* i18n キー (REQUIREMENT_GAP → requirementGap 等の lower camel) */
export const ISSUE_CAUSE_CATEGORY_OPTION_KEY = {
  REQUIREMENT_GAP: 'requirementGap',
  SPEC_CHANGE: 'specChange',
  DESIGN_DEFECT: 'designDefect',
  IMPLEMENTATION_ERROR: 'implementationError',
  TEST_GAP: 'testGap',
  EXTERNAL_DEPENDENCY: 'externalDependency',
  OPERATIONAL: 'operational',
  OTHER: 'other'
} as const satisfies Record<IssueCauseCategory, string>;

// ---------------------------------------------------------------------------
// Issue Overview (不具合 概要タブ用集計)
// ---------------------------------------------------------------------------

export type GetIssueOverviewQuery = ApiSearchQuery<'/v2/qa-force/issues/overview', 'get'>;
export type GetIssueOverviewResponse = ApiResponseBody<'/v2/qa-force/issues/overview', 'get'>;

// ---------------------------------------------------------------------------
// Issue Scope (Production 詳細 > 不具合スコープ編集) API Types
// ---------------------------------------------------------------------------

/** 不具合スコープ追加候補 Issue 一覧の query（productionId は path 由来で固定注入）。 */
export type GetAssignableIssuesQuery = ApiSearchQuery<'/v2/pn-force/productions/{id}/assignable-issues', 'get'>;
export type GetAssignableIssuesResponse = ApiResponseBody<'/v2/pn-force/productions/{id}/assignable-issues', 'get'>;
/** 不具合スコープへ Issue を一括追加する body。 */
export type PostScopeIssuesRequest = ApiRequestBody<'/v2/pn-force/productions/{id}/scope-issues', 'post'>;
export type PostScopeIssuesResponse = ApiResponseBody<'/v2/pn-force/productions/{id}/scope-issues', 'post'>;
export type DeleteScopeIssueResponse = ApiResponseBody<'/v2/pn-force/productions/{id}/scope-issues/{issueId}', 'delete'>;